/**
 * Resolve warehouse `inventoryListId` from SFCC store id (WhsCode) without writing cookies.
 */

import {useMemo} from 'react';
import {useStores} from '@salesforce/commerce-sdk-react';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';

import {pickInventoryListIdFromStore} from '../utils/warehouse-inventory-resolve';
import {
  isInventoryListIdAllowedByPreference,
  isWarehouseResolutionPreferenceEnabled,
} from '../utils/warehouse-inventory-cookies';

const STORES_STALE_MS = 24 * 60 * 60 * 1000;

/**
 * @param {Object} [options]
 * @param {string|undefined|null} [options.whsCode] - SFCC store id from Shipping API
 * @param {boolean} [options.enabled=true]
 * @returns {{
 *   inventoryListId: string|null,
 *   isLoading: boolean,
 *   isFetching: boolean,
 *   isSuccess: boolean,
 *   isError: boolean,
 *   store: Object|undefined,
 * }}
 */
export function useResolvedInventoryListId({whsCode, enabled = true} = {}) {
  const {site} = useMultiSite();
  const preferences = site?.preferences || {};
  const prefGate = isWarehouseResolutionPreferenceEnabled(preferences);
  const shouldQuery = Boolean(enabled && prefGate && whsCode);

  const storesResult = useStores(
    {
      parameters: {ids: whsCode},
    },
    {
      enabled: shouldQuery,
      staleTime: STORES_STALE_MS,
    }
  );

  const store = storesResult.data?.data?.[0];
  const inventoryListId = useMemo(() => pickInventoryListIdFromStore(store), [store]);
  const allowListOk = useMemo(
    () => isInventoryListIdAllowedByPreference(preferences, inventoryListId),
    [preferences, inventoryListId]
  );

  return {
    inventoryListId: allowListOk ? inventoryListId : null,
    isLoading: shouldQuery && storesResult.isLoading,
    isFetching: shouldQuery && storesResult.isFetching,
    isSuccess: !shouldQuery || storesResult.isSuccess,
    isError: storesResult.isError,
    store,
  };
}

export default useResolvedInventoryListId;
